import { Device } from "mediasoup-client";

const socket = io();

const $ = id => document.getElementById(id);
const urlParams = new URLSearchParams(location.search);
const forceTurn = urlParams.get("turn") === "1";

let config = { iceServers: [], maxParticipants: 6 };
let device = null;
let localStream = null;
let localProducer = null;
let sendTransport = null;
let recvTransport = null;
let audioContext = null;
let analyser = null;
let roomInfo = null;
let statsTimer = null;
let roomParticipants = new Map();
const consumers = new Map();
const remoteMeters = new Map();
let remoteAudioContext = null;
const remoteAudios = new Map();

function updateRemoteMeters() {
  for (const [consumerId, meter] of remoteMeters) {
    const analyser = meter.analyser;
    const data = meter.data;

    analyser.getByteTimeDomainData(data);

    let sum = 0;
    for (const x of data) {
      const v = (x - 128) / 128;
      sum += v * v;
    }

    const rms = Math.sqrt(sum / data.length);
    const db = rms > 0 ? 20 * Math.log10(rms) : -60;

    meter.level = Math.max(0, Math.min(100, ((db + 60) / 60) * 100));
    meter.db = Math.round(db);

    const fill = document.querySelector(
      `[data-meter-peer="${CSS.escape(meter.peerId)}"]`
    );
    const dbLabel = document.querySelector(
      `[data-db-peer="${CSS.escape(meter.peerId)}"]`
    );

    if (fill) {
      fill.style.width = `${meter.level}%`;
    }

    if (dbLabel) {
      dbLabel.textContent = `${meter.db} dB`;
    }
  }

  requestAnimationFrame(updateRemoteMeters);
}

requestAnimationFrame(updateRemoteMeters);

if (urlParams.get("room")) $("roomId").value = urlParams.get("room");
if (urlParams.get("token")) $("token").value = urlParams.get("token");

function request(event, data = {}) {
  return new Promise((resolve, reject) => {
    socket.emit(event, data, result => {
      if (!result?.ok) reject(new Error(result?.error || `Error en ${event}`));
      else resolve(result);
    });
  });
}

$("role").addEventListener("change", () => {
  $("studioAdmin").classList.toggle("hidden", $("role").value !== "studio");
});

$("connectBtn").onclick = connect;
$("disconnectBtn").onclick = disconnect;
$("muteBtn").onclick = toggleMute;
$("quality").onchange = applyQuality;
$("micSelect").onchange = restartLocalAudio;
$("aec").onchange = restartLocalAudio;
$("noise").onchange = restartLocalAudio;
$("agc").onchange = restartLocalAudio;

$("createRoomBtn").onclick = async () => {
  try {
    const data = await fetch("/api/rooms", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        adminKey: $("adminKey").value,
        name: $("roomName").value || "Programa en vivo"
      })
    }).then(async r => {
      const body = await r.json();
      if (!r.ok) throw new Error(body.error || "No se pudo crear la sala.");
      return body;
    });

    $("inviteResult").classList.remove("hidden");
    $("inviteUrl").value = data.inviteUrl;
    const u = new URL(data.inviteUrl);
    $("roomId").value = u.searchParams.get("room");
    $("token").value = u.searchParams.get("token");
  } catch (e) {
    $("setupError").textContent = e.message;
  }
};

$("copyInviteBtn").onclick = async () => {
  try {
    await navigator.clipboard.writeText($("inviteUrl").value);
    $("copyInviteBtn").textContent = "Copiado ✓";
  } catch {
    $("setupError").textContent = "No se pudo copiar automáticamente.";
  }
};

async function connect() {
  $("setupError").textContent = "";

  if (!window.isSecureContext && location.hostname !== "localhost") {
    $("setupError").textContent = "Se requiere HTTPS para usar el micrófono.";
    return;
  }

  const roomId = $("roomId").value.trim();
  const token = $("token").value.trim();
  const name = $("name").value.trim() || "Invitado";
  const role = $("role").value;

  if (!roomId || !token) {
    $("setupError").textContent = "Faltan ID de sala y token.";
    return;
  }

  try {
    config = await fetch(
      `/api/config?client=${encodeURIComponent(name)}`,
      { cache: "no-store" }
    ).then(r => r.json());

    await loadDevices();
    await openLocalAudio();

    device = await Device.factory();

    const joined = await request("join-room", {
      roomId,
      token,
      role,
      displayName: name
    });

    roomInfo = joined;

    await device.load({
      routerRtpCapabilities: joined.routerRtpCapabilities
    });

    await createSendTransport();
    await createRecvTransport();

    $("roomTitle").textContent = joined.roomName;
    $("roleLabel").textContent =
      joined.role === "studio" ? "MODO ESTUDIO" : "MODO INVITADO";
    $("transportInfo").textContent =
      `WebRTC SFU · TURN ${forceTurn ? "FORZADO" : "DISPONIBLE"}`;

    $("setup").classList.add("hidden");
    $("console").classList.remove("hidden");
    setStatus("Conectando…");

    updateParticipants(joined.participants || []);

    await produceLocalAudio();

    for (const producer of joined.existingProducers || []) {
      await consumeProducer(producer);
    }

    const producerList = await request("get-producers");
    for (const producer of producerList.producers) {
      await consumeProducer(producer);
    }

    startMeters();
    statsTimer = setInterval(updateStats, 1500);
    setStatus("Conectado");
  } catch (error) {
    console.error(error);
    $("setupError").textContent = error.message;
    await cleanupMedia();
  }
}

async function createSendTransport() {
  const result = await request("create-transport", { direction: "send" });
  const params = {
    ...result.params,
    iceServers: config.iceServers || [],
    iceTransportPolicy: forceTurn ? "relay" : "all"
  };

  sendTransport = device.createSendTransport(params);

  sendTransport.on("connect", async ({ dtlsParameters }, callback, errback) => {
    try {
      await request("connect-transport", {
        transportId: sendTransport.id,
        dtlsParameters
      });
      callback();
    } catch (e) {
      errback(e);
    }
  });

  sendTransport.on("produce", async ({ kind, rtpParameters, appData }, callback, errback) => {
    try {
      const result = await request("produce", {
        transportId: sendTransport.id,
        kind,
        rtpParameters,
        appData
      });
      callback({ id: result.id });
    } catch (e) {
      errback(e);
    }
  });

  sendTransport.on("connectionstatechange", state => {
    if (["failed", "disconnected"].includes(state)) {
      setStatus("Enlace inestable");
    } else if (state === "connected") {
      setStatus("Conectado");
    }
  });
}

async function createRecvTransport() {
  const result = await request("create-transport", { direction: "recv" });
  const params = {
    ...result.params,
    iceServers: config.iceServers || [],
    iceTransportPolicy: forceTurn ? "relay" : "all"
  };

  recvTransport = device.createRecvTransport(params);

  recvTransport.on("connect", async ({ dtlsParameters }, callback, errback) => {
    try {
      await request("connect-transport", {
        transportId: recvTransport.id,
        dtlsParameters
      });
      callback();
    } catch (e) {
      errback(e);
    }
  });

  recvTransport.on("connectionstatechange", state => {
    if (["failed", "disconnected"].includes(state)) {
      setStatus("Retorno inestable");
    }
  });
}

async function produceLocalAudio() {
  const track = localStream?.getAudioTracks()[0];
  if (!track) throw new Error("No hay pista de audio local.");

  const value = $("quality").value === "auto" ? 64000 : Number($("quality").value);

  localProducer = await sendTransport.produce({
    track,
    encodings: [{ maxBitrate: value }],
    codecOptions: {
      opusStereo: false,
      opusFec: true,
      opusDtx: false,
      opusMaxPlaybackRate: 48000,
      opusMaxAverageBitrate: value,
      opusPtime: 20
    },
    appData: {
      source: "microphone"
    }
  });
}

async function consumeProducer(producer) {
  if (!recvTransport || !device || consumers.has(producer.producerId)) return;

  try {
    const result = await request("consume", {
      transportId: recvTransport.id,
      producerId: producer.producerId,
      rtpCapabilities: device.recvRtpCapabilities
    });

    const consumer = await recvTransport.consume(result.params);

    consumers.set(consumer.id, {
      consumer,
      producerId: producer.producerId,
      peerId: result.source.peerId,
      displayName: result.source.displayName,
      role: result.source.role
    });

    const audio = document.createElement("audio");
    audio.autoplay = true;
    audio.playsInline = true;
    audio.dataset.consumerId = consumer.id;

    if (result.source.role === "studio" && roomInfo?.role === "guest") {
      audio.muted = roomInfo.returnEnabled === false;
    }

    const remoteStream = new MediaStream([consumer.track]);
    audio.srcObject = remoteStream;

    if (!remoteAudioContext || remoteAudioContext.state === "closed") {
      remoteAudioContext = new AudioContext();
    }

    const remoteSource = remoteAudioContext.createMediaStreamSource(remoteStream);
    const remoteAnalyser = remoteAudioContext.createAnalyser();
    remoteAnalyser.fftSize = 512;
    remoteSource.connect(remoteAnalyser);

    remoteMeters.set(consumer.id, {
      analyser: remoteAnalyser,
      data: new Uint8Array(remoteAnalyser.frequencyBinCount),
      peerId: result.source.peerId
    });

    if (audio.setSinkId && $("outputSelect").value) {
      await audio.setSinkId($("outputSelect").value).catch(() => {});
    }

    document.body.appendChild(audio);
    remoteAudios.set(consumer.id, audio);

    consumer.on("transportclose", () => removeConsumer(consumer.id));
    consumer.on("producerclose", () => removeConsumer(consumer.id));

    await request("resume-consumer", {
      consumerId: consumer.id
    });

    renderRemoteState();
  } catch (e) {
    console.warn("consumeProducer", e);
  }
}

function removeConsumer(consumerId) {
  const audio = remoteAudios.get(consumerId);
  if (audio) {
    audio.srcObject = null;
    audio.remove();
  }
  remoteAudios.delete(consumerId);
  consumers.delete(consumerId);
  renderRemoteState();
}

socket.on("new-producer", async producer => {
  await consumeProducer(producer);
});

socket.on("producer-closed", ({ consumerId }) => {
  if (consumerId) removeConsumer(consumerId);
});

socket.on("peer-left", ({ socketId }) => {
  for (const [consumerId, entry] of consumers) {
    if (entry.peerId === socketId) removeConsumer(consumerId);
  }
  roomParticipants.delete(socketId);
  renderRoomParticipants();
});

socket.on("studio-mute", ({ muted }) => {
  if (!localStream) return;

  const track = localStream.getAudioTracks()[0];
  if (!track) return;

  track.enabled = !muted;

  $("muteBtn").textContent = track.enabled ? "Silenciar" : "Activar micrófono";
});

socket.on("studio-return", ({ enabled }) => {
  for (const [consumerId, entry] of consumers) {
    if (entry.role !== "studio") continue;

    const audio = remoteAudios.get(consumerId);
    if (audio) {
      audio.muted = !enabled;
    }
  }
});

socket.on("kicked", () => {
  alert("Has sido expulsado de la sala por el Estudio.");
  disconnect();
});

socket.on("studio-left", () => {
  setStatus("Estudio desconectado");
});

socket.on("room-state", ({ participants }) => {
  const list = participants || [];
  updateParticipants(list);

  if (roomInfo?.role === "guest" && socket?.id) {
    const me = list.find(p => p.socketId === socket.id);
    if (me) {
      roomInfo.returnEnabled = me.returnEnabled !== false;
    }
  }
});

async function loadDevices() {
  try {
    const temp = await navigator.mediaDevices.getUserMedia({ audio: true });
    temp.getTracks().forEach(t => t.stop());
  } catch {}

  const devices = await navigator.mediaDevices.enumerateDevices();

  const inputs = devices.filter(d => d.kind === "audioinput");
  const outputs = devices.filter(d => d.kind === "audiooutput");

  $("micSelect").innerHTML = inputs.map(d =>
    `<option value="${escapeHtml(d.deviceId)}">${escapeHtml(d.label || "Micrófono")}</option>`
  ).join("");

  $("outputSelect").innerHTML = outputs.map(d =>
    `<option value="${escapeHtml(d.deviceId)}">${escapeHtml(d.label || "Salida de audio")}</option>`
  ).join("");

  $("outputSelect").onchange = async () => {
    for (const audio of remoteAudios.values()) {
      if (audio.setSinkId) await audio.setSinkId($("outputSelect").value).catch(() => {});
    }
  };
}

async function openLocalAudio() {
  stopLocalAudio();

  localStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      deviceId: $("micSelect").value
        ? { exact: $("micSelect").value }
        : undefined,
      echoCancellation: $("aec").checked,
      noiseSuppression: $("noise").checked,
      autoGainControl: $("agc").checked,
      channelCount: 1,
      sampleRate: 48000
    },
    video: false
  });
}

async function restartLocalAudio() {
  if (!localProducer) return;

  try {
    const oldTrack = localStream?.getAudioTracks()[0];
    if (oldTrack) oldTrack.stop();

    await openLocalAudio();

    const newTrack = localStream.getAudioTracks()[0];

    await localProducer.replaceTrack({
      track: newTrack
    });

    startMeters();
  } catch (e) {
    $("setupError").textContent = e.message;
  }
}

function stopLocalAudio() {
  localStream?.getTracks().forEach(track => track.stop());
  localStream = null;
}

async function toggleMute() {
  const track = localStream?.getAudioTracks()[0];
  if (!track || !localProducer) return;

  track.enabled = !track.enabled;

  if (track.enabled) {
    await localProducer.resume();
  } else {
    await localProducer.pause();
  }

  $("muteBtn").textContent = track.enabled
    ? "Silenciar"
    : "Activar micrófono";

  socket.emit("set-mute", { muted: !track.enabled });
}

async function applyQuality() {
  if (!localProducer) return;

  if ($("quality").value === "auto") return;

  const maxBitrate = Number($("quality").value);

  try {
    await localProducer.setRtpEncodingParameters({
      maxBitrate
    });
  } catch (e) {
    console.warn("No se pudo aplicar bitrate:", e);
  }
}

async function updateStats() {
  if (!sendTransport || !recvTransport) return;

  try {
    const reports = await sendTransport.getStats();
    let rtt = null;
    let bitrate = null;
    let loss = null;

    reports.forEach(report => {
      if (
        report.type === "candidate-pair" &&
        report.state === "succeeded" &&
        report.currentRoundTripTime
      ) {
        rtt = report.currentRoundTripTime * 1000;
      }

      if (report.type === "outbound-rtp" && report.kind === "audio") {
        const packetsSent = report.packetsSent || 0;
        const bytesSent = report.bytesSent || 0;
        bitrate = report.timestamp ? bytesSent : null;
        void packetsSent;
      }

      if (report.type === "remote-inbound-rtp" && report.kind === "audio") {
        if (report.packetsLost != null && report.packetsSent) {
          loss = Math.max(
            0,
            report.packetsLost /
              Math.max(1, report.packetsSent + report.packetsLost)
          ) * 100;
        }
      }
    });

    if (rtt != null) {
      $("rtt").textContent = `${Math.round(rtt)} ms`;
      const quality =
        rtt < 100 ? "Excelente" :
        rtt < 180 ? "Buena" :
        rtt < 300 ? "Inestable" : "Mala";

      $("network").textContent = `Red: ${quality}`;
      $("connectionQuality").textContent = quality.toUpperCase();
      $("latency").textContent = `Latencia: ${Math.round(rtt)} ms`;
    }

    if (loss != null) {
      $("loss").textContent = `${loss.toFixed(2)} %`;
    }

    if (bitrate != null) {
      $("bitrate").textContent = "Activo";
    }

    if ($("quality").value === "auto") {
      await adaptQuality(rtt, loss);
    }
  } catch (e) {
    console.warn("stats", e);
  }
}

let lastAutoBitrate = 64000;

async function adaptQuality(rtt, loss) {
  if (!localProducer) return;

  let target = 64000;

  if ((rtt || 0) > 300 || (loss || 0) > 5) {
    target = 24000;
  } else if ((rtt || 0) > 180 || (loss || 0) > 2) {
    target = 32000;
  } else if ((rtt || 0) > 120 || (loss || 0) > 1) {
    target = 48000;
  }

  if (target !== lastAutoBitrate) {
    lastAutoBitrate = target;
    await localProducer.setRtpEncodingParameters({
      maxBitrate: target
    }).catch(() => {});
  }

  $("bitrate").textContent = `${Math.round(target / 1000)} kbps`;
}

function startMeters() {
  if (!localStream) return;

  audioContext?.close();

  audioContext = new AudioContext();
  const source = audioContext.createMediaStreamSource(localStream);
  analyser = audioContext.createAnalyser();
  analyser.fftSize = 512;
  source.connect(analyser);

  const data = new Uint8Array(analyser.frequencyBinCount);

  function tick() {
    if (!analyser) return;

    analyser.getByteTimeDomainData(data);

    let sum = 0;
    for (const x of data) {
      const v = (x - 128) / 128;
      sum += v * v;
    }

    const rms = Math.sqrt(sum / data.length);
    const pct = Math.min(100, rms * 220);

    $("inputMeter").style.width = `${pct}%`;
    $("inputDb").textContent =
      rms > 0 ? `${Math.round(20 * Math.log10(rms))} dB` : "— dB";

    requestAnimationFrame(tick);
  }

  tick();
}

function updateParticipants(participants) {
  roomParticipants.clear();

  for (const participant of participants) {
    roomParticipants.set(participant.socketId, participant);
  }

  renderRoomParticipants();
}

function renderRoomParticipants() {
  const participants = [...roomParticipants.values()]
    .filter(p => p.socketId !== socket.id);

  $("participantCount").textContent = participants.length;

  const isStudio = roomInfo?.role === "studio";

  $("participants").innerHTML = participants.map(p => `
    <div class="participant">
      <div>
        <span class="dot"></span>
        <strong>${escapeHtml(p.displayName)}</strong>
      </div>

      <div class="muted">
        ${p.role === "studio" ? "ESTUDIO" : "INVITADO"}
      </div>

      <div>
        ${p.muted ? "🔇 Silenciado" : "🎙 Activo"}
      </div>

      <div class="participantMeter">
        <div class="participantMeterTrack">
          <div
            class="participantMeterFill"
            data-meter-peer="${escapeHtml(p.socketId)}"
            style="width: 0%">
          </div>
        </div>
        <span class="participantDb" data-db-peer="${escapeHtml(p.socketId)}">— dB</span>
      </div>

      ${isStudio && p.role !== "studio" ? `
        <div class="participantControls">
          <button
            class="secondary participantMuteBtn"
            data-socket-id="${escapeHtml(p.socketId)}"
            data-muted="${p.muted ? "false" : "true"}">
            ${p.muted ? "🎙 Activar" : "🔇 Silenciar"}
          </button>

          <button
            class="secondary participantReturnBtn"
            data-socket-id="${escapeHtml(p.socketId)}"
            data-return-enabled="${p.returnEnabled !== false ? "false" : "true"}">
            ${p.returnEnabled !== false ? "🔇 Retorno OFF" : "🔊 Retorno ON"}
          </button>

          <button
            class="danger participantKickBtn"
            data-socket-id="${escapeHtml(p.socketId)}">
            ❌ Expulsar
          </button>
        </div>
      ` : ""}
    </div>
  `).join("");

  document.querySelectorAll(".participantMuteBtn").forEach(button => {
    button.onclick = async () => {
      try {
        await request("studio-mute-peer", {
          targetSocketId: button.dataset.socketId,
          muted: button.dataset.muted === "true"
        });
      } catch (error) {
        console.error(error);
        setStatus(error.message);
      }
    };
  });

  document.querySelectorAll(".participantReturnBtn").forEach(button => {
    button.onclick = async () => {
      try {
        await request("studio-return-peer", {
          targetSocketId: button.dataset.socketId,
          enabled: button.dataset.returnEnabled === "true"
        });
      } catch (error) {
        console.error(error);
      }
    };
  });

  document.querySelectorAll(".participantKickBtn").forEach(button => {
    button.onclick = async () => {
      const participant = roomParticipants.get(button.dataset.socketId);
      const name = participant?.displayName || "este participante";

      if (!confirm(`¿Expulsar a ${name}?`)) return;

      try {
        await request("studio-kick-peer", {
          targetSocketId: button.dataset.socketId
        });
      } catch (error) {
        console.error(error);
        setStatus(error.message);
      }
    };
  });
}

function renderRemoteState() {
  // Mantiene la interfaz sencilla en v0.2.
}

function setStatus(text) {
  $("statusBadge").textContent = text;
  $("statusBadge").style.background =
    text === "Conectado" ? "#29a944" :
    text.includes("inestable") ? "#c8921e" :
    "#666";
}

async function cleanupMedia() {
  if (statsTimer) clearInterval(statsTimer);
  statsTimer = null;

  for (const entry of consumers.values()) {
    entry.consumer.close();
  }
  consumers.clear();

  for (const audio of remoteAudios.values()) {
    audio.srcObject = null;
    audio.remove();
  }
  remoteAudios.clear();

  localProducer?.close();
  localProducer = null;

  sendTransport?.close();
  recvTransport?.close();
  sendTransport = null;
  recvTransport = null;

  stopLocalAudio();

  if (audioContext) {
    await audioContext.close().catch(() => {});
    audioContext = null;
  }
}

async function disconnect() {
  await cleanupMedia();

  socket.disconnect();
  location.reload();
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, char => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;"
  }[char]));
}
